import React, { useRef, useEffect, useCallback } from 'react';
import { FINISHES } from './types';

interface PreviewCanvasProps {
  croppedImageUrl: string | null;
  selectedFinish: string;
  selectedMaterial: string;
  borderColor: string;
  onPreviewReady: (dataUrl: string) => void;
}

const PreviewCanvas: React.FC<PreviewCanvasProps> = ({
  croppedImageUrl,
  selectedFinish,
  selectedMaterial,
  borderColor,
  onPreviewReady
}) => {
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const renderPreviewCanvas = useCallback(async (): Promise<void> => {
    if (!croppedImageUrl || !previewCanvasRef.current) return;

    const canvas = previewCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const previewWidth = 600;
    const previewHeight = 600;
    canvas.width = previewWidth;
    canvas.height = previewHeight;

    ctx.clearRect(0, 0, previewWidth, previewHeight);

    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject();
      img.src = croppedImageUrl;
    });

    const imgAspectRatio = img.width / img.height;
    const canvasAspectRatio = previewWidth / previewHeight;
    
    let drawWidth: number, drawHeight: number, offsetX: number, offsetY: number;
    
    if (imgAspectRatio > canvasAspectRatio) {
      drawWidth = previewWidth * 0.7;
      drawHeight = drawWidth / imgAspectRatio;
    } else {
      drawHeight = previewHeight * 0.7;
      drawWidth = drawHeight * imgAspectRatio;
    }
    
    offsetX = (previewWidth - drawWidth) / 2;
    offsetY = (previewHeight - drawHeight) / 2;

    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;

    const finishObj = FINISHES.find(f => f.value === selectedFinish);
    const hasFrame = finishObj && finishObj.value.includes('floater');
    const canvasWrapColor = borderColor || '#ffffff';
    const darkenHex = (hex: string, amount: number): string => {
      const clean = hex.replace('#', '');
      if (clean.length !== 6) return hex;
      const num = parseInt(clean, 16);
      const r = Math.max(0, (num >> 16) - amount);
      const g = Math.max(0, ((num >> 8) & 0x00ff) - amount);
      const b = Math.max(0, (num & 0x0000ff) - amount);
      return `#${(((r << 16) | (g << 8) | b)).toString(16).padStart(6, '0')}`;
    };

    if (hasFrame) {
      const frameWidth = 12;
      const gapWidth = 6;
      const frameColor = '#2a2a2a';
      const gapColor = '#1a1a1a';
      
      const frameOffsetX = offsetX - frameWidth - gapWidth;
      const frameOffsetY = offsetY - frameWidth - gapWidth;
      const frameTotalWidth = drawWidth + (frameWidth * 2) + (gapWidth * 2);
      const frameTotalHeight = drawHeight + (frameWidth * 2) + (gapWidth * 2);
      
      ctx.fillStyle = gapColor;
      ctx.fillRect(frameOffsetX + frameWidth, frameOffsetY + frameWidth, 
                   drawWidth + (gapWidth * 2), gapWidth);
      ctx.fillRect(frameOffsetX + frameWidth, frameOffsetY + frameTotalHeight - frameWidth - gapWidth, 
                   drawWidth + (gapWidth * 2), gapWidth);
      ctx.fillRect(frameOffsetX + frameWidth, frameOffsetY + frameWidth, 
                   gapWidth, drawHeight + (gapWidth * 2));
      ctx.fillRect(frameOffsetX + frameTotalWidth - frameWidth - gapWidth, frameOffsetY + frameWidth, 
                   gapWidth, drawHeight + (gapWidth * 2));
      
      ctx.fillStyle = frameColor;
      ctx.fillRect(frameOffsetX, frameOffsetY, 
                   frameTotalWidth, frameWidth);
      ctx.fillRect(frameOffsetX, frameOffsetY + frameTotalHeight - frameWidth, 
                   frameTotalWidth, frameWidth);
      ctx.fillRect(frameOffsetX, frameOffsetY, 
                   frameWidth, frameTotalHeight);
      ctx.fillRect(frameOffsetX + frameTotalWidth - frameWidth, frameOffsetY, 
                   frameWidth, frameTotalHeight);
      
      const edgeWidth = 8;
      const bottomGradient = ctx.createLinearGradient(
        frameOffsetX, 
        frameOffsetY + frameTotalHeight - edgeWidth, 
        frameOffsetX, 
        frameOffsetY + frameTotalHeight + edgeWidth
      );
      bottomGradient.addColorStop(0, '#4a4a4a');
      bottomGradient.addColorStop(0.5, '#1a1a1a');
      bottomGradient.addColorStop(1, '#000000');
      ctx.fillStyle = bottomGradient;
      ctx.fillRect(frameOffsetX, frameOffsetY + frameTotalHeight, frameTotalWidth + edgeWidth, edgeWidth);
      
      const rightGradient = ctx.createLinearGradient(
        frameOffsetX + frameTotalWidth - edgeWidth, 
        frameOffsetY, 
        frameOffsetX + frameTotalWidth + edgeWidth, 
        frameOffsetY
      );
      rightGradient.addColorStop(0, '#4a4a4a');
      rightGradient.addColorStop(0.5, '#1a1a1a');
      rightGradient.addColorStop(1, '#000000');
      ctx.fillStyle = rightGradient;
      ctx.fillRect(frameOffsetX + frameTotalWidth, frameOffsetY, edgeWidth, frameTotalHeight + edgeWidth);
      
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = 10;
      ctx.shadowOffsetX = 5;
      ctx.shadowOffsetY = 5;
      
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
      ctx.fillRect(frameOffsetX - 2, frameOffsetY - 2, 
                   frameTotalWidth + 4, frameTotalHeight + 4);
      
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      
      ctx.strokeStyle = '#1a1a1a';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(frameOffsetX, frameOffsetY + frameTotalHeight + edgeWidth);
      ctx.lineTo(frameOffsetX + frameTotalWidth + edgeWidth, frameOffsetY + frameTotalHeight + edgeWidth);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(frameOffsetX + frameTotalWidth + edgeWidth, frameOffsetY);
      ctx.lineTo(frameOffsetX + frameTotalWidth + edgeWidth, frameOffsetY + frameTotalHeight + edgeWidth);
      ctx.stroke();
    } else if (selectedMaterial !== 'Canvas') {
      const borderWidth = 2;
      ctx.fillStyle = canvasWrapColor;
      ctx.fillRect(offsetX - borderWidth, offsetY - borderWidth,
                   drawWidth + (borderWidth * 2), drawHeight + (borderWidth * 2));
    }

    ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

    // Canvas light/shadow and weave texture overlay
    if (selectedMaterial === 'Canvas') {
      // Light falloff from top-right
      const light = ctx.createRadialGradient(
        offsetX + drawWidth * 0.85,
        offsetY + drawHeight * 0.15,
        drawWidth * 0.05,
        offsetX + drawWidth * 0.85,
        offsetY + drawHeight * 0.15,
        drawWidth * 0.9
      );
      light.addColorStop(0, 'rgba(255, 255, 255, 0.22)');
      light.addColorStop(0.35, 'rgba(255, 255, 255, 0.08)');
      light.addColorStop(1, 'rgba(0, 0, 0, 0.10)');
      ctx.fillStyle = light;
      ctx.fillRect(offsetX, offsetY, drawWidth, drawHeight);

      // Weave texture (denser crosshatch)
      const weaveSpacing = 2;
      ctx.globalAlpha = 0.10;
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 0.35;
      for (let x = offsetX - drawHeight * 0.02; x < offsetX + drawWidth + drawHeight * 0.02; x += weaveSpacing) {
        ctx.beginPath();
        ctx.moveTo(x, offsetY);
        ctx.lineTo(x + drawHeight * 0.02, offsetY + drawHeight);
        ctx.stroke();
      }
      for (let y = offsetY - drawWidth * 0.02; y < offsetY + drawHeight + drawWidth * 0.02; y += weaveSpacing) {
        ctx.beginPath();
        ctx.moveTo(offsetX, y);
        ctx.lineTo(offsetX + drawWidth, y + drawWidth * 0.02);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }

    if (selectedMaterial === 'Canvas') {
      const imageData = ctx.getImageData(offsetX, offsetY, drawWidth, drawHeight);
      const data = imageData.data;
      
      for (let i = 0; i < data.length; i += 4) {
        const noise = (Math.random() - 0.5) * 5;
        data[i] = Math.max(0, Math.min(255, data[i] + noise));
        data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + noise));
        data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + noise));
      }
      
      ctx.putImageData(imageData, offsetX, offsetY);

      if (!hasFrame) {
        const depth = 8; // thinner wrap
        const skew = 1; // 45 degree perspective
        const edgeDark = darkenHex(canvasWrapColor, 25);
        const edgeDarker = darkenHex(canvasWrapColor, 50);

        // Right face (parallelogram)
        const rightGrad = ctx.createLinearGradient(offsetX + drawWidth, offsetY, offsetX + drawWidth + depth, offsetY + depth * skew);
        rightGrad.addColorStop(0, canvasWrapColor);
        rightGrad.addColorStop(0.6, edgeDark);
        rightGrad.addColorStop(1, edgeDarker);
        ctx.fillStyle = rightGrad;
        ctx.beginPath();
        ctx.moveTo(offsetX + drawWidth, offsetY);
        ctx.lineTo(offsetX + drawWidth + depth, offsetY + depth * skew);
        ctx.lineTo(offsetX + drawWidth + depth, offsetY + drawHeight + depth * skew);
        ctx.lineTo(offsetX + drawWidth, offsetY + drawHeight);
        ctx.closePath();
        ctx.fill();

        // Bottom face (parallelogram)
        const bottomGrad = ctx.createLinearGradient(offsetX, offsetY + drawHeight, offsetX, offsetY + drawHeight + depth * skew);
        bottomGrad.addColorStop(0, canvasWrapColor);
        bottomGrad.addColorStop(0.6, edgeDark);
        bottomGrad.addColorStop(1, edgeDarker);
        ctx.fillStyle = bottomGrad;
        ctx.beginPath();
        ctx.moveTo(offsetX, offsetY + drawHeight);
        ctx.lineTo(offsetX + drawWidth, offsetY + drawHeight);
        ctx.lineTo(offsetX + drawWidth + depth, offsetY + drawHeight + depth * skew);
        ctx.lineTo(offsetX + depth, offsetY + drawHeight + depth * skew);
        ctx.closePath();
        ctx.fill();

        // Subtle shadow under the wrap
        ctx.shadowColor = 'rgba(0, 0, 0, 0.08)';
        ctx.shadowBlur = 6;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;
        ctx.fillStyle = 'transparent';
        ctx.fillRect(offsetX + drawWidth, offsetY + drawHeight, depth, depth);
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
      }
    } else {
      const edgeWidth = 3;
      ctx.fillStyle = '#000000';
      ctx.fillRect(offsetX, offsetY + drawHeight, drawWidth + edgeWidth, edgeWidth);
      ctx.fillRect(offsetX + drawWidth, offsetY, edgeWidth, drawHeight + edgeWidth);
    }

    const dataUrl = canvas.toDataURL('image/png');
    onPreviewReady(dataUrl);
  }, [croppedImageUrl, selectedFinish, selectedMaterial, borderColor, onPreviewReady]);

  useEffect(() => {
    if (croppedImageUrl) {
      renderPreviewCanvas();
    }
  }, [croppedImageUrl, selectedFinish, selectedMaterial, borderColor, renderPreviewCanvas]);

  return (
    <canvas 
      ref={previewCanvasRef} 
      className="preview-canvas"
      style={{ display: 'none' }}
    />
  );
};

export default PreviewCanvas;
